export const INPUT_PATH_PREFIX = 'input.';

export const ANALYSIS_PATH_PREFIX = 'analysis.summary.';

export type TemplatePlaceholder = {
  path: string;
  labelKey: string;
};

export const TEMPLATE_PLACEHOLDERS: readonly TemplatePlaceholder[] = [
  { path: 'customer.name', labelKey: 'customerName' },
  { path: 'customer.phone', labelKey: 'customerPhone' },
  { path: 'agent.name', labelKey: 'agentName' },
  { path: 'campaign.name', labelKey: 'campaignName' },
  { path: 'company.name', labelKey: 'companyName' },
];

export const POST_CALL_TEMPLATE_PLACEHOLDERS: readonly TemplatePlaceholder[] =
  TEMPLATE_PLACEHOLDERS.filter((placeholder) => !placeholder.path.startsWith('agent.'));

const LEGACY_KEY_MAP: Record<string, string> = {
  agent_human_name: 'agent.name',
  company_name: 'company.name',
  campaign_name: 'campaign.name',
};

const PATH_PATTERN = /\{\{([^}]+)}}/g;
const LEGACY_PATTERN = /\[\[%([^%]*)%]]/g;
const INPUT_NAME_PATTERN = /^[a-zA-Z][a-zA-Z0-9_]*$/;

export const wrapPath = (path: string) => `{{${path}}}`;

export const analysisPath = (field: string) => `${ANALYSIS_PATH_PREFIX}${field}`;

export const shortenPath = (path: string): string => {
  for (const prefix of [INPUT_PATH_PREFIX, ANALYSIS_PATH_PREFIX]) {
    if (path.startsWith(prefix)) {
      return path.slice(prefix.length);
    }
  }

  return path;
};

export const collectTemplatePaths = (template: string): string[] => {
  const paths: string[] = [];

  for (const match of template.matchAll(PATH_PATTERN)) {
    const path = match[1].trim();

    if (path && !paths.includes(path)) {
      paths.push(path);
    }
  }

  return paths;
};

export const deriveInputNames = (template: string): string[] =>
  collectTemplatePaths(template)
    .filter((path) => path.startsWith(INPUT_PATH_PREFIX))
    .map((path) => path.slice(INPUT_PATH_PREFIX.length))
    .filter(Boolean);

export const findInvalidInputNames = (template: string): string[] =>
  deriveInputNames(template).filter((name) => !INPUT_NAME_PATTERN.test(name));

export const findLegacyPlaceholders = (template: string): string[] => {
  const leftovers: string[] = [];

  for (const match of template.matchAll(LEGACY_PATTERN)) {
    if (!leftovers.includes(match[0])) {
      leftovers.push(match[0]);
    }
  }

  return leftovers;
};

export const convertLegacyTemplate = (template: string): string =>
  template.replace(LEGACY_PATTERN, (match, rawKey: string) => {
    const path = LEGACY_KEY_MAP[rawKey.trim()];

    return path ? wrapPath(path) : match;
  });

export const hasLegacySyntax = (template: string): boolean =>
  findLegacyPlaceholders(template).length > 0;

const resolvePath = (context: Record<string, unknown>, path: string): unknown =>
  path.split('.').reduce<unknown>((current, segment) => {
    if (current === null || current === undefined || typeof current !== 'object') {
      return undefined;
    }

    return (current as Record<string, unknown>)[segment];
  }, context);

const formatValue = (value: unknown): string => {
  if (value === undefined || value === null) {
    return '';
  }

  return typeof value === 'object' ? JSON.stringify(value) : String(value);
};

export type InterpolateOptions = {
  onMissing?: (path: string) => string;
};

export type TemplateSegmentKind = 'text' | 'value' | 'missing';

export type TemplateSegment = {
  text: string;
  kind: TemplateSegmentKind;
};

export const buildTemplateSegments = (
  template: string,
  context: Record<string, unknown>,
  options: InterpolateOptions = {},
): TemplateSegment[] => {
  const segments: TemplateSegment[] = [];
  let lastIndex = 0;

  for (const match of template.matchAll(PATH_PATTERN)) {
    const start = match.index ?? 0;

    if (start > lastIndex) {
      segments.push({ text: template.slice(lastIndex, start), kind: 'text' });
    }

    const path = match[1].trim();
    const resolved = resolvePath(context, path);

    if (resolved === undefined || resolved === null) {
      segments.push({ text: options.onMissing ? options.onMissing(path) : '', kind: 'missing' });
    } else {
      segments.push({ text: formatValue(resolved), kind: 'value' });
    }

    lastIndex = start + match[0].length;
  }

  if (lastIndex < template.length) {
    segments.push({ text: template.slice(lastIndex), kind: 'text' });
  }

  return segments;
};

export const interpolateTemplate = (
  template: string,
  context: Record<string, unknown>,
  options: InterpolateOptions = {},
): string =>
  buildTemplateSegments(template, context, options)
    .map((segment) => segment.text)
    .join('');
